import { z } from 'zod';
import { formatarBRL, percentualDe, reaisParaCentavos, type Centavos } from './dinheiro.js';

/**
 * A Ponte é composta por 100 cadeiras distribuídas em três camadas.
 * Valores e quantidades vêm do protótipo original (docs/prototipo/index.html) e da
 * Carta de Intenções — não altere sem alterar também o texto contratual.
 */
export const CAMADAS_IDS = ['movimento', 'socio', 'circulo'] as const;
export const camadaSchema = z.enum(CAMADAS_IDS);
export type CamadaId = z.infer<typeof camadaSchema>;

export interface Camada {
  id: CamadaId;
  nome: string;
  /** Investimento anual da cadeira, em centavos. */
  valor: Centavos;
  /** Quantidade de cadeiras existentes na camada. */
  cadeiras: number;
}

export const CAMADAS: Record<CamadaId, Camada> = {
  movimento: {
    id: 'movimento',
    nome: 'Movimento Prosperar',
    valor: reaisParaCentavos(120_000),
    cadeiras: 55,
  },
  socio: { id: 'socio', nome: 'Sócio', valor: reaisParaCentavos(240_000), cadeiras: 30 },
  circulo: { id: 'circulo', nome: 'Círculo', valor: reaisParaCentavos(360_000), cadeiras: 15 },
};

export const TOTAL_DE_CADEIRAS = Object.values(CAMADAS).reduce((s, c) => s + c.cadeiras, 0); // 100

/**
 * Ordem em que as camadas ocupam a numeração das 100 cadeiras: Círculo 001–015,
 * Sócio 016–045, Movimento Prosperar 046–100.
 *
 * O número é da **comunidade inteira**, não um contador dentro da camada — a cadeira 020 é
 * uma só, e é de Sócio. Por isso a faixa é derivada daqui e da lotação de cada camada, e
 * não escrita à mão: mudar `cadeiras` sem mexer nas faixas deixaria buraco ou sobreposição
 * entre elas, e duas camadas reivindicando o mesmo número é cadeira vendida duas vezes.
 */
export const ORDEM_DA_NUMERACAO = [
  'circulo',
  'socio',
  'movimento',
] as const satisfies readonly CamadaId[];

export interface FaixaDeCadeiras {
  /** Primeiro número da camada, inclusive. */
  primeira: number;
  /** Último número da camada, inclusive. */
  ultima: number;
}

export const FAIXAS: Record<CamadaId, FaixaDeCadeiras> = (() => {
  const faixas = {} as Record<CamadaId, FaixaDeCadeiras>;
  let proxima = 1;
  for (const id of ORDEM_DA_NUMERACAO) {
    const ultima = proxima + CAMADAS[id].cadeiras - 1;
    faixas[id] = { primeira: proxima, ultima };
    proxima = ultima + 1;
  }
  return faixas;
})();

export function faixaDaCamada(camada: CamadaId): FaixaDeCadeiras {
  return FAIXAS[camada];
}

/** A que camada pertence um número de cadeira. `null` fora de 001–100. */
export function camadaDoNumero(entrada: string | number): CamadaId | null {
  const numero = Number(entrada);
  if (!Number.isInteger(numero)) return null;
  return (
    ORDEM_DA_NUMERACAO.find((id) => numero >= FAIXAS[id].primeira && numero <= FAIXAS[id].ultima) ??
    null
  );
}

/**
 * Sinal pago junto da Carta de Intenções, à vista, sem parcelamento.
 *
 * É o **padrão** da tabela, não uma constante do contrato: a operação pode negociar outro
 * valor de sinal até o link de pagamento ser gerado (ver `sinalNegociado` em
 * candidato.ts). Quem quer saber quanto o sinal representa de fato numa ficha usa
 * `percentualDoSinal(valoresVigentes(candidato))`, nunca esta constante.
 */
export const PERCENTUAL_SINAL = 5;
/** Saldo quitado após a aprovação em curadoria, no Termo de Aceite. */
export const PERCENTUAL_SALDO = 100 - PERCENTUAL_SINAL;

/** Prazo, em dias corridos, para pagamento do sinal antes de a pré-reserva cair. */
export const DIAS_VENCIMENTO_SINAL = 3;
/** Prazo, em dias corridos, para pagamento do saldo. */
export const DIAS_VENCIMENTO_SALDO = 7;

export interface ValoresDaCadeira {
  total: Centavos;
  sinal: Centavos;
  saldo: Centavos;
}

/**
 * Sinal e saldo de uma camada, **pela tabela vigente hoje**.
 *
 * Para candidato que já teve a Carta emitida, não use esta função: use
 * `valoresVigentes(candidato)`, que devolve o valor congelado no contrato. A tabela viva
 * só vale para ficha que ainda não virou documento.
 *
 * O saldo é o resto da subtração — nunca um segundo arredondamento — para que
 * sinal + saldo seja sempre exatamente o total.
 */
export function valoresDaCamada(camada: CamadaId): ValoresDaCadeira {
  const total = CAMADAS[camada].valor;
  const sinal = percentualDe(total, PERCENTUAL_SINAL);
  return { total, sinal, saldo: total - sinal };
}

/**
 * Recalcula sinal e saldo a partir de um sinal negociado, mantendo o total.
 *
 * O saldo é sempre o resto da subtração, como em `valoresDaCamada`: é o que garante que
 * sinal + saldo continue batendo com o investimento anual depois de o operador mexer no
 * sinal. Nenhuma tela pode fazer essa conta por fora.
 */
export function comSinalDe(total: Centavos, sinal: Centavos): ValoresDaCadeira {
  return { total, sinal, saldo: total - sinal };
}

/**
 * Quanto o sinal representa do investimento anual, em pontos percentuais.
 *
 * Vale 5 na tabela padrão e qualquer outra coisa quando o sinal foi negociado — é o número
 * que a Carta imprime na cl. 3.1 e que os rótulos do cockpit mostram. Derivar do valor,
 * em vez de repetir `PERCENTUAL_SINAL`, é o que impede o documento de dizer "5%" ao lado
 * de um sinal que não é 5%.
 */
export function percentualDoSinal(valores: Pick<ValoresDaCadeira, 'total' | 'sinal'>): number {
  if (valores.total <= 0) return 0;
  return (valores.sinal * 100) / valores.total;
}

export interface SinalInvalido {
  motivo: string;
}

/**
 * `null` quando o sinal negociado é aceitável para o total contratado.
 *
 * Os dois limites são de contrato, não de tela: sinal zero não confere pré-reserva
 * nenhuma (Carta, cl. 3.1) e o gateway recusa cobrança de R$ 0,00; sinal igual ao total
 * deixaria o Termo de Aceite sem saldo a quitar, e a esteira trava com uma cobrança de
 * saldo que o Asaas não aceita emitir.
 */
export function validarSinal(total: Centavos, sinal: Centavos): SinalInvalido | null {
  if (!Number.isInteger(sinal)) {
    return { motivo: 'O sinal é gravado em centavos inteiros.' };
  }
  if (sinal <= 0) {
    return { motivo: 'O sinal precisa ser maior que zero — é ele que garante a pré-reserva.' };
  }
  if (sinal >= total) {
    return {
      motivo:
        `O sinal precisa ser menor que o investimento anual (${formatarBRL(total)}): ` +
        'o saldo é etapa própria da esteira e não pode ficar zerado.',
    };
  }
  return null;
}

/**
 * Número da cadeira: inteiro de 1 a 100, normalizado com três dígitos.
 *
 * A normalização importa além da estética: a reserva exclusiva usa
 * `CADEIRA#<camada>#<numero>` como chave, e "1" e "001" seriam **duas cadeiras
 * diferentes** — dois integrantes com a mesma cadeira e nenhum erro aparente.
 */
export function normalizarNumeroDaCadeira(entrada: string): string {
  return String(Number(entrada.trim())).padStart(3, '0');
}

export interface NumeroDaCadeiraInvalido {
  motivo: string;
}

/** `null` quando o número é aceitável para a camada. */
export function validarNumeroDaCadeira(
  camada: CamadaId,
  entrada: string,
): NumeroDaCadeiraInvalido | null {
  const limpo = entrada.trim();
  if (limpo === '') return null; // número em branco é permitido até a cadeira ser ocupada

  if (!/^\d{1,3}$/.test(limpo)) {
    return { motivo: 'Informe apenas dígitos, sem letras nem pontuação.' };
  }

  const numero = Number(limpo);
  const { primeira, ultima } = FAIXAS[camada];

  if (numero < primeira || numero > ultima) {
    const dono = camadaDoNumero(numero);
    const dela = dono
      ? ` A cadeira ${formatarNumeroDaCadeira(numero)} é da camada ${CAMADAS[dono].nome}.`
      : '';
    return {
      motivo:
        `A camada ${CAMADAS[camada].nome} vai da cadeira ${formatarNumeroDaCadeira(primeira)} ` +
        `até a ${formatarNumeroDaCadeira(ultima)}.${dela}`,
    };
  }

  return null;
}

/** Três dígitos, como a cadeira é escrita em tela e em documento. */
export function formatarNumeroDaCadeira(numero: number): string {
  return String(numero).padStart(3, '0');
}

/** "001 a 015", para a dica do campo e para a mensagem de recusa. */
export function faixaPorExtenso(camada: CamadaId): string {
  const { primeira, ultima } = FAIXAS[camada];
  return `${formatarNumeroDaCadeira(primeira)} a ${formatarNumeroDaCadeira(ultima)}`;
}
